import React, { useState, useMemo } from 'react';
import {
    Box,
    Paper,
    Slider,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import StorageIcon from '@mui/icons-material/Storage';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import ThermostatIcon from '@mui/icons-material/Thermostat';
import AirIcon from '@mui/icons-material/Air';

export interface IprNodalAnalisisProps {
    isCompact?: boolean;
}

const yacimientoFields = [
    { label: 'Pr (psi)', name: 'staticPressure' as const, icon: <TrendingUpIcon sx={{ fontSize: 12 }} /> },
    { label: 'Pb (psi)', name: 'bubblePoint' as const, icon: <WaterDropIcon sx={{ fontSize: 12 }} /> },
    { label: 'Twf (°F)', name: 'bottomholeTemp' as const, icon: <ThermostatIcon sx={{ fontSize: 12 }} /> },
    { label: 'BS&W (%)', name: 'waterCut' as const, icon: <WaterDropIcon sx={{ fontSize: 12 }} /> },
    { label: 'GOR (scf/stb)', name: 'gor' as const, icon: <AirIcon sx={{ fontSize: 12 }} /> },
    { label: 'J (BFPD/psi)', name: 'productivityIndex' as const, icon: <TrendingUpIcon sx={{ fontSize: 12 }} /> },
];

export const IprNodalAnalisis = ({ isCompact = true }: IprNodalAnalisisProps) => {
    const [liftSystem, setLiftSystem] = useState<'BCP' | 'BM'>('BCP');
    const [params, setParams] = useState({
        staticPressure: 1850,
        bubblePoint: 1200,
        bottomholeTemp: 165,
        waterCut: 50,
        gor: 350,
        productivityIndex: 1.5,
        bcpRPM: 250,
        bcpK: 0.8,
        bcpEff: 85,
        bmSPM: 8.5,
        bmStroke: 120,
        bmPlunger: 1.75,
        bmEff: 80,
    });

    const nodalCalcs = useMemo(() => {
        let qActual = 0;
        if (liftSystem === 'BCP') {
            qActual = params.bcpRPM * params.bcpK * (params.bcpEff / 100);
        } else {
            const area = Math.PI * Math.pow(params.bmPlunger / 2, 2);
            qActual = (params.bmSPM * params.bmStroke * area * 0.1484) * (params.bmEff / 100);
        }
        const qMax = params.productivityIndex * params.staticPressure;
        const iprPoints: { q: number; pwf: number }[] = [];
        for (let q = 0; q <= qMax * 1.2; q += qMax / 20) {
            let pwf = params.staticPressure - q / params.productivityIndex;
            if (pwf < 0) pwf = 0;
            iprPoints.push({ q, pwf });
        }
        const pwfCalculated = Math.max(0, params.staticPressure - qActual / params.productivityIndex);
        return {
            qActual: parseFloat(qActual.toFixed(1)),
            qMax: parseFloat(qMax.toFixed(1)),
            iprPoints,
            efficiency: parseFloat(((qActual / qMax) * 100).toFixed(1)),
            pwfCalculated: parseFloat(pwfCalculated.toFixed(0)),
        };
    }, [params, liftSystem]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        const key = name as keyof typeof params;
        setParams((prev) => ({ ...prev, [key]: parseFloat(value) || 0 }));
    };

    const qMaxScaled = nodalCalcs.qMax * 1.2 || 1;
    const iprPolyline = nodalCalcs.iprPoints
        .map((p) => `${(p.q / qMaxScaled) * 500},${250 - (p.pwf / params.staticPressure) * 250}`)
        .join(' ');
    const intakePath = `M ${(nodalCalcs.qActual / qMaxScaled) * 480} 250 Q ${(nodalCalcs.qActual / qMaxScaled) * 500} 125 ${(nodalCalcs.qActual / qMaxScaled) * 510} 0`;
    const opPointX = (nodalCalcs.qActual / qMaxScaled) * 500;
    const opPointY = 250 - (nodalCalcs.pwfCalculated / params.staticPressure) * 250;

    const kpiCards = (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1 }}>
            <Paper elevation={0} sx={{ p: 1, border: '1px solid', borderColor: 'rgba(16,185,129,0.3)', bgcolor: 'rgba(16,185,129,0.08)', textAlign: 'center' }}>
                <Typography variant="caption" sx={{ color: '#059669', fontWeight: 700, fontSize: 8, textTransform: 'uppercase' }}>Potencial Máx (AOF)</Typography>
                <Typography variant="body2" fontWeight={700} sx={{ color: '#059669', fontSize: 12 }}>{nodalCalcs.qMax} <Typography component="span" variant="caption" sx={{ fontSize: 9 }}>BPD</Typography></Typography>
            </Paper>
            <Paper elevation={0} sx={{ p: 1, border: '1px solid', borderColor: 'rgba(59,130,246,0.3)', bgcolor: 'rgba(59,130,246,0.08)', textAlign: 'center' }}>
                <Typography variant="caption" sx={{ color: '#2563eb', fontWeight: 700, fontSize: 8, textTransform: 'uppercase' }}>Caudal Operativo</Typography>
                <Typography variant="body2" fontWeight={700} sx={{ color: '#2563eb', fontSize: 12 }}>{nodalCalcs.qActual} <Typography component="span" variant="caption" sx={{ fontSize: 9 }}>BPD</Typography></Typography>
            </Paper>
            <Paper elevation={1} sx={{ p: 1, bgcolor: 'grey.900', textAlign: 'center' }}>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 8, textTransform: 'uppercase' }}>Eficiencia</Typography>
                <Typography variant="body2" fontWeight={700} color="white" sx={{ fontSize: 12 }}>{nodalCalcs.efficiency} <Typography component="span" variant="caption" color="inherit" sx={{ fontSize: 9 }}>%</Typography></Typography>
            </Paper>
        </Box>
    );

    const chartSection = (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flex: 1, minHeight: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase' }}>
                    Gráfico IPR vs. Intake
                </Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'success.main' }} />
                        <Typography variant="caption" sx={{ fontSize: 9 }}>IPR</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'error.main' }} />
                        <Typography variant="caption" sx={{ fontSize: 9 }}>Intake ({liftSystem})</Typography>
                    </Box>
                </Box>
            </Box>
            <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', pb: 0.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
                    Pwf Actual (psi): <strong>{nodalCalcs.pwfCalculated}</strong>
                </Typography>
            </Box>
            <Box sx={{ flex: 1, minHeight: 180, position: 'relative' }}>
                <Box
                    component="svg"
                    viewBox="0 0 500 250"
                    preserveAspectRatio="xMidYMid meet"
                    sx={{ width: '100%', height: '100%', overflow: 'visible' }}
                >
                    {[...Array(6)].map((_, i) => (
                        <g key={i}>
                            <line x1="0" y1={250 - i * 50} x2="500" y2={250 - i * 50} stroke="#f1f5f9" strokeWidth="1.5" />
                            <text x="-35" y={255 - i * 50} fontSize="9" fill="#94a3b8" fontWeight="bold">
                                {(i * (params.staticPressure / 5)).toFixed(0)}
                            </text>
                        </g>
                    ))}
                    <text x="-50" y="125" transform="rotate(-90, -50, 125)" fontSize="9" fill="#64748b" fontWeight="bold">
                        Pwf [psi]
                    </text>
                    <text x="250" y="275" textAnchor="middle" fontSize="9" fill="#64748b" fontWeight="bold">
                        Q [BFPD]
                    </text>
                    <polyline fill="none" stroke="#10b981" strokeWidth="3" strokeLinecap="round" points={iprPolyline} />
                    <path fill="none" stroke="#ef4444" strokeWidth="3" strokeLinecap="round" d={intakePath} />
                    <g transform={`translate(${opPointX}, ${opPointY})`}>
                        <circle r="6" fill="#ef4444" stroke="white" strokeWidth="2" />
                        <rect x="8" y="-28" width="95" height="24" rx="4" fill="#1e293b" />
                        <text x="55" y="-12" textAnchor="middle" fontSize="9" fill="white" fontWeight="bold">
                            {nodalCalcs.qActual} BPD @ {nodalCalcs.pwfCalculated} psi
                        </text>
                    </g>
                </Box>
            </Box>
            {kpiCards}
        </Box>
    );

    const inputSection = (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, overflowY: 'auto', minHeight: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                <StorageIcon sx={{ fontSize: 16, color: 'success.main' }} />
                <Typography variant="caption" fontWeight={700} color="success.main" sx={{ textTransform: 'uppercase' }}>
                    Propiedades del Yacimiento
                </Typography>
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                {yacimientoFields.map((field) => (
                    <Box key={field.name}>
                        <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5, minHeight: 32 }}>
                            {field.icon} {field.label}
                        </Typography>
                        <TextField
                            size="small"
                            type="number"
                            name={field.name}
                            value={params[field.name]}
                            onChange={handleInputChange}
                            inputProps={{ step: '0.1' }}
                            fullWidth
                            sx={{ '& .MuiInputBase-input': { fontSize: 12, py: 0.5 } }}
                        />
                    </Box>
                ))}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1, borderBottom: '1px solid', borderColor: 'divider', pt: 0.5 }}>
                <SettingsIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                <Typography variant="caption" fontWeight={700} color="primary.main" sx={{ textTransform: 'uppercase' }}>
                    Configuración de Equipo
                </Typography>
            </Box>
            <Box sx={{ mb: 1.5 }}>
                <ToggleButtonGroup
                    size="small"
                    value={liftSystem}
                    exclusive
                    onChange={(_, v) => v && setLiftSystem(v)}
                    sx={{ '& .MuiToggleButton-root': { py: 0.25, px: 1.5, fontSize: 10 } }}
                >
                    <ToggleButton value="BCP">BCP</ToggleButton>
                    <ToggleButton value="BM">BM</ToggleButton>
                </ToggleButtonGroup>
            </Box>
            {liftSystem === 'BCP' ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                            <Typography variant="caption" fontWeight={700} color="text.secondary">RPM</Typography>
                            <Typography variant="caption" fontWeight={700} color="primary.main">{params.bcpRPM}</Typography>
                        </Box>
                        <Slider
                            name="bcpRPM"
                            size="small"
                            value={params.bcpRPM}
                            onChange={(_, v) => setParams((p) => ({ ...p, bcpRPM: v as number }))}
                            min={0}
                            max={500}
                            valueLabelDisplay="auto"
                        />
                    </Box>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                        <Box>
                            <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ display: 'block', mb: 0.5, minHeight: 32 }}>Factor K (BPD/RPM)</Typography>
                            <TextField size="small" type="number" name="bcpK" value={params.bcpK} onChange={handleInputChange} inputProps={{ step: 0.1 }} fullWidth sx={{ '& .MuiInputBase-input': { fontSize: 12 } }} />
                        </Box>
                        <Box>
                            <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ display: 'block', mb: 0.5, minHeight: 32 }}>Eficiencia Vol. (%)</Typography>
                            <TextField size="small" type="number" name="bcpEff" value={params.bcpEff} onChange={handleInputChange} fullWidth sx={{ '& .MuiInputBase-input': { fontSize: 12 } }} />
                        </Box>
                    </Box>
                </Box>
            ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                            <Typography variant="caption" fontWeight={700} color="text.secondary">SPM</Typography>
                            <Typography variant="caption" fontWeight={700} color="primary.main">{params.bmSPM}</Typography>
                        </Box>
                        <Slider
                            name="bmSPM"
                            size="small"
                            value={params.bmSPM}
                            onChange={(_, v) => setParams((p) => ({ ...p, bmSPM: v as number }))}
                            min={0}
                            max={25}
                            step={0.1}
                            valueLabelDisplay="auto"
                        />
                    </Box>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                        <Box>
                            <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ display: 'block', mb: 0.5, minHeight: 32 }}>Ø Pistón (in)</Typography>
                            <TextField size="small" type="number" name="bmPlunger" value={params.bmPlunger} onChange={handleInputChange} inputProps={{ step: 0.125 }} fullWidth sx={{ '& .MuiInputBase-input': { fontSize: 12 } }} />
                        </Box>
                        <Box>
                            <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ display: 'block', mb: 0.5, minHeight: 32 }}>Eficiencia (%)</Typography>
                            <TextField size="small" type="number" name="bmEff" value={params.bmEff} onChange={handleInputChange} fullWidth sx={{ '& .MuiInputBase-input': { fontSize: 12 } }} />
                        </Box>
                    </Box>
                    <Box>
                        <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ display: 'block', mb: 0.5, minHeight: 32 }}>Carrera (in)</Typography>
                        <TextField size="small" type="number" name="bmStroke" value={params.bmStroke} onChange={handleInputChange} fullWidth sx={{ '& .MuiInputBase-input': { fontSize: 12 } }} />
                    </Box>
                </Box>
            )}
        </Box>
    );

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden', bgcolor: 'white' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, borderBottom: '1px solid', borderColor: 'divider', px: 1.5, py: 0.75, bgcolor: 'grey.50', flexShrink: 0 }}>
                <Box sx={{ bgcolor: 'grey.900', p: 0.75, borderRadius: 2 }}><TrendingUpIcon sx={{ fontSize: 14, color: 'primary.light' }} /></Box>
                <Box>
                    <Typography variant="caption" fontWeight={700} color="text.primary" sx={{ fontSize: 11 }}>Análisis Nodal Avanzado</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: 9, display: 'block' }}>Well Performance Diagnostic</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 'auto' }}>
                    <CalendarMonthIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>2025</Typography>
                </Box>
            </Box>

            <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden', minHeight: 0 }}>
                {/* Chart - left. Extended: flex to fill. Compact: max 420 */}
                <Box
                    sx={{
                        flex: isCompact ? '1 1 auto' : 2,
                        minWidth: 280,
                        maxWidth: isCompact ? 420 : 'none',
                        borderRight: '1px solid',
                        borderColor: 'divider',
                        p: 1.5,
                        display: 'flex',
                        flexDirection: 'column',
                        bgcolor: 'grey.50',
                    }}
                >
                    {chartSection}
                </Box>

                {/* Right: inputs. Compact: 200px. Extended: flex 1 to occupy remaining width */}
                <Box
                    sx={{
                        flex: isCompact ? '0 0 200px' : 1,
                        minWidth: isCompact ? 0 : 260,
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                    }}
                >
                    <Box sx={{ flex: 1, overflow: 'auto', p: 1.5, minHeight: 0 }}>
                        {inputSection}
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};
